import { useEffect, useState } from 'react';
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  MessageSquareText,
  RefreshCw,
  Search,
  Send,
  Users,
  X,
} from 'lucide-react';
import { locationsApi } from '@/lib/apiServices';
import { getPersianLabel, persianDisplayText } from '@/lib/persianUi';
import { persianDigits } from '@/features/reports/lib/format';
import { superAdminApi } from '../../api/superAdminApi';
import '../../styles/campaigns.css';

const ROLE_OPTIONS = [
  ['customer', 'مشتریان'],
  ['vendor', 'فروشندگان'],
  ['irancharsouq_admin', 'مدیران ایران چارسوق'],
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS);

const STATUS_LABELS = {
  queued: 'در صف',
  running: 'در حال ارسال',
  completed: 'تکمیل شد',
  completed_with_errors: 'تکمیل با خطا',
  failed: 'ناموفق',
};

const initialForm = {
  kind: 'announcement',
  message: '',
  roles: [],
  province_ids: [],
  city_ids: [],
};

const unwrapCollection = (result) => {
  const payload = result?.data ?? result;

  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  if (payload && Array.isArray(payload.data)) return payload.data;

  return [];
};

const getAllProvinceCities = async (provinceId) => {
  const cities = [];
  let page = 1;
  let pages = 1;

  while (page <= pages) {
    const result = await locationsApi.citiesByProvince(provinceId, { page, size: 100 });
    const response = result?.data ?? result;
    cities.push(...unwrapCollection(result));
    pages = Number(response?.pages ?? 1);
    page += 1;
  }

  return cities;
};

const formatCount = (value) => Number(value ?? 0).toLocaleString('fa-IR');
const matchesSearch = (value, search) => String(value ?? '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());

const estimateSmsParts = (message) => {
  if (!message) return 0;

  const characters = Array.from(message);
  const isAscii = characters.every((character) => character.codePointAt(0) < 128);
  const singlePartLimit = isAscii ? 160 : 70;
  const multiPartLimit = isAscii ? 153 : 67;

  return characters.length <= singlePartLimit
    ? 1
    : Math.ceil(characters.length / multiPartLimit);
};

export default function SmsCampaigns() {
  const [form, setForm] = useState(initialForm);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityCatalog, setCityCatalog] = useState(new Map());
  const [provinceSearch, setProvinceSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientTotal, setRecipientTotal] = useState(0);
  const [recipientPages, setRecipientPages] = useState(0);
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientError, setRecipientError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const audienceKey = JSON.stringify({
    roles: form.roles,
    province_ids: form.province_ids,
    city_ids: form.city_ids,
  });
  const hasAudienceSelection = Boolean(
    form.roles.length || form.province_ids.length || form.city_ids.length,
  );

  const loadCampaigns = async () => {
    const result = await superAdminApi.getCampaigns({ page: 1, size: 20 });
    setCampaigns(unwrapCollection(result));
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([
      locationsApi.provinces({ page: 1, size: 100 }),
      superAdminApi.getCampaigns({ page: 1, size: 20 }),
    ])
      .then(([provinceResult, campaignResult]) => {
        if (!mounted) return;
        setProvinces(unwrapCollection(provinceResult));
        setCampaigns(unwrapCollection(campaignResult));
        setLoading(false);
      })
      .catch((requestError) => {
        if (!mounted) return;
        console.error(requestError);
        setError('دریافت اطلاعات کمپین‌ها ناموفق بود');
        setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const selectedProvinceKey = form.province_ids.join(',');

  useEffect(() => {
    if (!selectedProvinceKey) return undefined;

    let mounted = true;
    const provinceIds = selectedProvinceKey.split(',').map(Number);
    Promise.all(provinceIds.map(getAllProvinceCities))
      .then((results) => {
        if (!mounted) return;
        const loadedCities = results.flat();
        setCities(loadedCities);
        setCityCatalog((current) => {
          const next = new Map(current);
          loadedCities.forEach((city) => next.set(city.id, city));
          return next;
        });
        setCitiesLoading(false);
      })
      .catch((requestError) => {
        if (!mounted) return;
        console.error(requestError);
        setError('دریافت شهرها ناموفق بود');
        setCitiesLoading(false);
      });

    return () => { mounted = false; };
  }, [selectedProvinceKey]);

  useEffect(() => {
    if (!hasAudienceSelection) return undefined;

    let mounted = true;
    const timeoutId = window.setTimeout(() => {
      superAdminApi.getCampaignRecipients(JSON.parse(audienceKey), {
        page: recipientPage,
        size: 10,
      })
        .then((result) => {
          if (!mounted) return;
          setRecipients(Array.isArray(result?.items) ? result.items : []);
          setRecipientTotal(Number(result?.total ?? 0));
          setRecipientPages(Number(result?.pages ?? 0));
          setRecipientLoading(false);
        })
        .catch((requestError) => {
          if (!mounted) return;
          console.error(requestError);
          setRecipients([]);
          setRecipientTotal(0);
          setRecipientPages(0);
          setRecipientError('دریافت فهرست گیرندگان ناموفق بود');
          setRecipientLoading(false);
        });
    }, 350);

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [audienceKey, hasAudienceSelection, recipientPage]);

  const audiencePayload = () => ({
    roles: form.roles.length ? form.roles : null,
    province_ids: form.province_ids.length ? form.province_ids : null,
    city_ids: form.city_ids.length ? form.city_ids : null,
  });

  const payload = () => ({
    kind: form.kind,
    message: form.message.trim(),
    ...audiencePayload(),
  });

  const prepareAudienceRefresh = (hasSelection) => {
    setRecipientPage(1);
    setRecipients([]);
    setRecipientTotal(0);
    setRecipientPages(0);
    setRecipientError('');
    setRecipientLoading(hasSelection);
    setPreview(null);
  };

  const toggleRole = (role, checked) => {
    const roles = checked
      ? [...new Set([...form.roles, role])]
      : form.roles.filter((item) => item !== role);
    prepareAudienceRefresh(Boolean(roles.length || form.province_ids.length || form.city_ids.length));
    setForm((current) => ({
      ...current,
      roles,
    }));
  };

  const toggleProvince = (provinceId, checked) => {
    const provinceIds = checked
      ? [...new Set([...form.province_ids, provinceId])]
      : form.province_ids.filter((item) => item !== provinceId);
    const hasSelection = Boolean(form.roles.length || provinceIds.length || form.city_ids.length);
    prepareAudienceRefresh(hasSelection);
    setCitiesLoading(provinceIds.length > 0);
    if (!provinceIds.length) setCities([]);
    setForm((current) => ({ ...current, province_ids: provinceIds }));
    setError('');
  };

  const toggleCity = (cityId, checked) => {
    const cityIds = checked
      ? [...new Set([...form.city_ids, cityId])]
      : form.city_ids.filter((item) => item !== cityId);
    const hasSelection = Boolean(form.roles.length || form.province_ids.length || cityIds.length);
    prepareAudienceRefresh(hasSelection);
    setForm((current) => ({ ...current, city_ids: cityIds }));
  };

  const toggleProvinceCities = (provinceId) => {
    const provinceCityIds = cities
      .filter((city) => city.parent_id === provinceId)
      .map((city) => city.id);
    if (!provinceCityIds.length) return;

    const allSelected = provinceCityIds.every((cityId) => form.city_ids.includes(cityId));
    const cityIds = allSelected
      ? form.city_ids.filter((cityId) => !provinceCityIds.includes(cityId))
      : [...new Set([...form.city_ids, ...provinceCityIds])];
    const hasSelection = Boolean(form.roles.length || form.province_ids.length || cityIds.length);
    prepareAudienceRefresh(hasSelection);
    setForm((current) => ({ ...current, city_ids: cityIds }));
  };

  const changeRecipientPage = (nextPage) => {
    setRecipientLoading(true);
    setRecipients([]);
    setRecipientError('');
    setRecipientPage(nextPage);
  };

  const requestPreview = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setPreview(await superAdminApi.previewCampaign(payload()));
    } catch (requestError) {
      console.error(requestError);
      setError('پیش‌نمایش کمپین ناموفق بود');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmCampaign = async () => {
    if (!preview || !preview.recipient_count || recipientLoading || preview.recipient_count !== recipientTotal) return;
    const confirmed = window.confirm(`ارسال برای ${preview.recipient_count} گیرنده در صف قرار بگیرد؟`);
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      await superAdminApi.createCampaign({
        ...payload(),
        expected_recipient_count: preview.recipient_count,
      });
      prepareAudienceRefresh(false);
      setForm(initialForm);
      setCities([]);
      setCitiesLoading(false);
      await loadCampaigns();
    } catch (requestError) {
      console.error(requestError);
      setError('ثبت کمپین ناموفق بود');
    } finally {
      setBusy(false);
    }
  };

  const refreshCampaigns = async () => {
    setLoading(true);
    setError('');
    try {
      await loadCampaigns();
    } catch (requestError) {
      console.error(requestError);
      setError('دریافت کمپین‌ها ناموفق بود');
    } finally {
      setLoading(false);
    }
  };

  const filteredProvinces = provinces.filter((province) => matchesSearch(province.name_fa, provinceSearch));
  const selectedProvinces = form.province_ids
    .map((provinceId) => provinces.find((province) => province.id === provinceId))
    .filter(Boolean);
  const selectedCities = form.city_ids
    .map((cityId) => cityCatalog.get(cityId))
    .filter(Boolean);

  return (
    <div className="campaign-page">
      <div className="admin-page-header campaign-page-header">
        <div>
          <h1 className="admin-page-title">پیام‌های گروهی</h1>
          <p className="admin-page-subtitle">اعلان‌ها و پیشنهادها</p>
        </div>
        <button type="button" className="campaign-refresh-btn" onClick={refreshCampaigns} disabled={loading}>
          <RefreshCw size={16} aria-hidden="true" />
          <span>بروزرسانی</span>
        </button>
      </div>

      {error && (
        <div className="campaign-error" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <form className="campaign-panel campaign-form" onSubmit={requestPreview}>
        <div className="campaign-form-grid">
          <div className="campaign-field">
            <label htmlFor="campaign-kind">نوع پیام</label>
            <select
              id="campaign-kind"
              className="campaign-control"
              value={form.kind}
              onChange={(event) => {
                setForm((current) => ({ ...current, kind: event.target.value }));
                setPreview(null);
              }}
            >
              <option value="announcement">اعلان</option>
              <option value="offer">پیشنهاد</option>
            </select>
          </div>

          <fieldset className="campaign-field campaign-roles-field">
            <legend>مخاطبان بر اساس نقش</legend>
            <div className="campaign-role-options">
              {ROLE_OPTIONS.map(([value, label]) => (
                <label className="campaign-role-option" key={value}>
                  <input
                    type="checkbox"
                    checked={form.roles.includes(value)}
                    onChange={(event) => toggleRole(value, event.target.checked)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="campaign-audience-fields">
            <div className="campaign-field">
              <label htmlFor="campaign-province-search">استان‌ها</label>
              <div className="campaign-search-field">
                <Search size={16} aria-hidden="true" />
                <input
                  id="campaign-province-search"
                  type="search"
                  value={provinceSearch}
                  onChange={(event) => setProvinceSearch(event.target.value)}
                  placeholder="جست‌وجوی استان"
                  aria-label="جست‌وجوی استان"
                />
              </div>
              <div className="campaign-option-list" role="group" aria-label="انتخاب استان‌ها">
                {filteredProvinces.length ? filteredProvinces.map((province) => (
                  <label className="campaign-check-option" key={province.id}>
                    <input
                      type="checkbox"
                      checked={form.province_ids.includes(province.id)}
                      onChange={(event) => toggleProvince(province.id, event.target.checked)}
                    />
                    <span>{province.name_fa}</span>
                  </label>
                )) : <p className="campaign-list-message">استانی با این نام پیدا نشد.</p>}
              </div>
              <span className="campaign-field-help">با انتخاب استان، همهٔ شهرهای آن در مخاطبان محاسبه می‌شوند.</span>
            </div>

            <div className="campaign-field">
              <label htmlFor="campaign-city-search">شهرها</label>
              <div className="campaign-search-field">
                <Search size={16} aria-hidden="true" />
                <input
                  id="campaign-city-search"
                  type="search"
                  value={citySearch}
                  onChange={(event) => setCitySearch(event.target.value)}
                  placeholder="جست‌وجوی شهر"
                  aria-label="جست‌وجوی شهر در استان‌های انتخاب‌شده"
                  disabled={!form.province_ids.length}
                />
              </div>
              <div className="campaign-city-list" aria-label="شهرهای استان‌های انتخاب‌شده">
                {!form.province_ids.length ? (
                  <p className="campaign-list-message">برای دیدن شهرها، ابتدا استان را انتخاب کنید.</p>
                ) : citiesLoading ? (
                  <p className="campaign-list-message" role="status">در حال دریافت شهرها...</p>
                ) : selectedProvinces.map((province) => {
                  const allProvinceCities = cities.filter((city) => city.parent_id === province.id);
                  const provinceCities = allProvinceCities.filter(
                    (city) => matchesSearch(city.name_fa, citySearch),
                  );
                  const allSelected = allProvinceCities.length > 0
                    && allProvinceCities.every((city) => form.city_ids.includes(city.id));

                  return (
                    <section className="campaign-city-group" key={province.id}>
                      <div className="campaign-city-group-heading">
                        <strong>{province.name_fa}</strong>
                        <button
                          type="button"
                          className="campaign-select-all-btn"
                          onClick={() => toggleProvinceCities(province.id)}
                          disabled={!allProvinceCities.length}
                        >
                          {allSelected ? 'حذف انتخاب شهرها' : 'انتخاب همه'}
                        </button>
                      </div>
                      <div className="campaign-city-options">
                        {provinceCities.length ? provinceCities.map((city) => (
                          <label className="campaign-check-option" key={city.id}>
                            <input
                              type="checkbox"
                              checked={form.city_ids.includes(city.id)}
                              onChange={(event) => toggleCity(city.id, event.target.checked)}
                            />
                            <span>{city.name_fa}</span>
                          </label>
                        )) : <p className="campaign-list-message">شهری با این نام پیدا نشد.</p>}
                      </div>
                    </section>
                  );
                })}
              </div>
              <span className="campaign-field-help">می‌توانید شهرهای مشخصی را جداگانه انتخاب کنید.</span>
            </div>

            <div className="campaign-selected-locations" aria-live="polite">
              <span className="campaign-selected-label">استان‌های انتخاب‌شده</span>
              <div className="campaign-selected-chips">
                {selectedProvinces.map((province) => (
                  <button
                    type="button"
                    className="campaign-selected-chip"
                    key={province.id}
                    onClick={() => toggleProvince(province.id, false)}
                    aria-label={`حذف استان ${province.name_fa}`}
                  >
                    <span>{province.name_fa}</span>
                    <X size={14} aria-hidden="true" />
                  </button>
                ))}
                {!selectedProvinces.length && <span className="campaign-no-selection">استانی انتخاب نشده</span>}
              </div>
              <span className="campaign-selected-label">شهرهای انتخاب‌شده</span>
              <div className="campaign-selected-chips">
                {selectedCities.map((city) => (
                  <button
                    type="button"
                    className="campaign-selected-chip campaign-city-chip"
                    key={city.id}
                    onClick={() => toggleCity(city.id, false)}
                    aria-label={`حذف شهر ${city.name_fa}`}
                  >
                    <span>{city.name_fa}</span>
                    <X size={14} aria-hidden="true" />
                  </button>
                ))}
                {!selectedCities.length && <span className="campaign-no-selection">شهری انتخاب نشده</span>}
              </div>
            </div>
          </div>

          <div className="campaign-field campaign-message-field">
            <label htmlFor="campaign-message">متن پیام</label>
            <textarea
              id="campaign-message"
              className="campaign-control campaign-message-input"
              rows={5}
              maxLength={500}
              value={form.message}
              onChange={(event) => {
                setForm((current) => ({ ...current, message: event.target.value }));
                setPreview(null);
              }}
              aria-describedby="campaign-message-meta"
            />
            <div className="campaign-message-meta" id="campaign-message-meta" aria-live="polite">
              <span>{formatCount(Array.from(form.message).length)} از ۵۰۰ نویسه</span>
              <span className="campaign-segment-count">
                <MessageSquareText size={15} aria-hidden="true" />
                {formatCount(preview?.segments_per_sms ?? estimateSmsParts(form.message))} بخش پیامک
              </span>
            </div>
          </div>
        </div>

        <div className="campaign-form-actions">
          <button type="submit" className="campaign-primary-btn" disabled={busy || !form.message.trim() || !hasAudienceSelection || recipientLoading || recipientTotal === 0}>
            <Megaphone size={17} aria-hidden="true" />
            <span>پیش‌نمایش گیرندگان</span>
          </button>
        </div>
      </form>

      <section className="campaign-panel campaign-recipients-panel" aria-live="polite">
        <div className="campaign-section-heading campaign-recipients-heading">
          <div>
            <span className="campaign-eyebrow">بررسی مخاطبان انتخاب‌شده</span>
            <h2>گیرندگان کمپین</h2>
          </div>
          <div className="campaign-recipient-total">
            <Users size={18} aria-hidden="true" />
            <strong>{formatCount(recipientTotal)}</strong>
            <span>گیرنده</span>
          </div>
        </div>

        {recipientError && <div className="campaign-error" role="alert">{recipientError}</div>}
        {!hasAudienceSelection ? (
          <div className="campaign-recipient-state">
            <Users size={22} aria-hidden="true" />
            <strong>مخاطبی انتخاب نشده است</strong>
            <span>برای مشاهدهٔ گیرندگان، یک نقش، استان یا شهر را انتخاب کنید.</span>
          </div>
        ) : recipientLoading ? (
          <div className="campaign-recipient-state" role="status">
            <RefreshCw size={20} className="campaign-loading-icon" aria-hidden="true" />
            <span>در حال به‌روزرسانی فهرست گیرندگان...</span>
          </div>
        ) : recipientError ? null : recipientTotal === 0 ? (
          <div className="campaign-recipient-state">
            <Users size={22} aria-hidden="true" />
            <strong>گیرنده‌ای با این انتخاب پیدا نشد</strong>
            <span>نقش یا محدودهٔ جغرافیایی دیگری را انتخاب کنید.</span>
          </div>
        ) : (
          <>
            <div className="campaign-table-wrap campaign-recipient-table-wrap">
              <table className="campaign-table campaign-recipient-table">
                <thead>
                  <tr><th>نام</th><th>نقش</th><th>استان</th><th>شهر</th><th>شمارهٔ ماسک‌شده</th></tr>
                </thead>
                <tbody>
                  {recipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td>{persianDisplayText(recipient.name, 'نامشخص')}</td>
                      <td>{recipient.roles.map((role) => getPersianLabel(ROLE_LABELS, role, 'campaign.roles')).join('، ')}</td>
                      <td>{persianDisplayText(recipient.province)}</td>
                      <td>{persianDisplayText(recipient.city)}</td>
                      <td dir="ltr" className="campaign-masked-phone">{persianDigits(recipient.masked_phone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {recipientPages > 1 && (
              <div className="campaign-recipient-pagination">
                <span>صفحهٔ {formatCount(recipientPage)} از {formatCount(recipientPages)}</span>
                <div>
                  <button
                    type="button"
                    aria-label="صفحهٔ بعد"
                    disabled={recipientPage >= recipientPages || recipientLoading}
                    onClick={() => changeRecipientPage(recipientPage + 1)}
                  >
                    <ChevronLeft size={17} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label="صفحهٔ قبل"
                    disabled={recipientPage <= 1 || recipientLoading}
                    onClick={() => changeRecipientPage(recipientPage - 1)}
                  >
                    <ChevronRight size={17} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {preview && (
        <section className="campaign-panel campaign-preview" aria-live="polite">
          <div className="campaign-section-heading">
            <div>
              <span className="campaign-eyebrow">بررسی پیش از ارسال</span>
              <h2>پیش‌نمایش کمپین</h2>
            </div>
            <MessageSquareText size={20} aria-hidden="true" />
          </div>

          <div className="campaign-preview-summary">
            <div className="campaign-recipient-count">
              <Users size={20} aria-hidden="true" />
              <div>
                <span>تعداد گیرندگان</span>
                <strong>{formatCount(preview.recipient_count)}</strong>
              </div>
            </div>
            <div className="campaign-preview-detail">
              <span>بخش پیامک برای هر گیرنده</span>
              <strong>{formatCount(preview.segments_per_sms)}</strong>
            </div>
          </div>

          {preview.exceeds_limit && (
            <div className="campaign-limit-warning" role="status">
              تعداد گیرندگان از سقف {formatCount(preview.max_recipients)} بیشتر است؛ مخاطبان را محدودتر کنید.
            </div>
          )}
          {preview.sample_phones?.length > 0 && (
            <p className="campaign-sample-phones">نمونه شماره‌ها: {preview.sample_phones.join('، ')}</p>
          )}
          <pre className="campaign-message-preview">{preview.final_message}</pre>
          <div className="campaign-preview-actions">
            <button
              type="button"
              className="campaign-primary-btn"
              onClick={confirmCampaign}
              disabled={busy || preview.exceeds_limit || !preview.recipient_count || recipientLoading || recipientTotal !== preview.recipient_count}
            >
              <Send size={16} aria-hidden="true" />
              <span>تایید و قرار دادن در صف</span>
            </button>
          </div>
          {!recipientLoading && recipientTotal !== preview.recipient_count && (
            <p className="campaign-count-mismatch" role="status">
              تعداد گیرندگان تغییر کرده است؛ دوباره پیش‌نمایش بگیرید.
            </p>
          )}
        </section>
      )}

      <section className="campaign-panel campaign-history">
        <div className="campaign-section-heading campaign-history-heading">
          <div>
            <span className="campaign-eyebrow">پیگیری ارسال‌ها</span>
            <h2>وضعیت کمپین‌ها</h2>
          </div>
          <span className="campaign-history-count">{formatCount(campaigns.length)} کمپین</span>
        </div>
        <div className="campaign-table-wrap">
          <table className="campaign-table">
            <thead>
              <tr><th>نوع</th><th>مخاطبان</th><th>وضعیت</th><th>گیرندگان</th><th>ارسال‌شده</th><th>ناموفق</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6">
                    <div className="campaign-table-state" role="status">
                      <RefreshCw size={19} className="campaign-loading-icon" aria-hidden="true" />
                      <span>در حال دریافت اطلاعات کمپین‌ها...</span>
                    </div>
                  </td>
                </tr>
              ) : campaigns.length ? campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>{getPersianLabel({ offer: 'پیشنهاد', announcement: 'اعلان' }, campaign.kind, 'campaign.kind')}</td>
                  <td>
                    <div className="campaign-history-audience">
                      {campaign.audience_roles?.map((role) => (
                        <span key={role}>{getPersianLabel(ROLE_LABELS, role, 'campaign.roles')}</span>
                      ))}
                      {campaign.audience_province_names?.map((province) => (
                        <span key={`province-${province}`}>{persianDisplayText(province, 'استان نامشخص')}</span>
                      ))}
                      {campaign.audience_city_names?.map((city) => (
                        <span key={`city-${city}`}>{persianDisplayText(city, 'شهر نامشخص')}</span>
                      ))}
                      {!campaign.audience_roles?.length
                        && !campaign.audience_province_names?.length
                        && !campaign.audience_city_names?.length
                        && <span>همهٔ مخاطبان واجد شرایط</span>}
                    </div>
                  </td>
                  <td><span className={`campaign-status campaign-status-${campaign.status.replaceAll('_', '-')}`}>
                    {getPersianLabel(STATUS_LABELS, campaign.status, 'campaign.status')}
                  </span></td>
                  <td>{formatCount(campaign.total)}</td>
                  <td>{formatCount(campaign.sent_count)}</td>
                  <td>{formatCount(campaign.failed_count)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6">
                    <div className="campaign-table-state campaign-empty-state">
                      <span className="campaign-empty-icon"><Megaphone size={21} aria-hidden="true" /></span>
                      <strong>هنوز کمپینی ثبت نشده است</strong>
                      <span>پس از ایجاد کمپین، وضعیت ارسال آن در این فهرست نمایش داده می‌شود.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}